<template>
  <div class="world-editor-room-info">
    <!-- Full Container Drop Zone -->
    <div
        ref="dropZone"
        class="room-drop-zone"
    >
      <div class="drop-zone-overlay"
           @dragover.prevent
           @dragenter.prevent="handleDragEnter"
           @dragleave.prevent="handleDragLeave"
           @drop.prevent="handleEntityDrop">
        <div class="drop-zone-message">
          <svg class="drop-zone-icon" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M12 2.6l8 4.2v10.4l-8 4.2-8-4.2V6.8z"/>
            <path d="M4 6.8l8 4.2 8-4.2M12 11v10.4"/>
          </svg>
          <span class="drop-zone-text">Drop Item or NPC Here</span>
        </div>
      </div>
    </div>

    <div class="room-info-header">
      <h3 class="gm-section-title">Room Information</h3>
      <button class="gm-btn gm-btn--sm gm-btn--ghost close-btn" title="Close" @click="closePanel">
        <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 4.5l7 7M11.5 4.5l-7 7"/></svg>
      </button>
    </div>

    <div class="room-info-content">
      <!-- Basic Room Info -->
      <BasicInformation
          :room="room"
          :editable="editable"
          @update:room-type="updateRoomType"
      />

      <!-- Room Labels -->
      <Labels
          :busy="labelBusy"
          :catalog="labelCatalog"
          :conflicts="labelConflicts"
          :editable="editable"
          :labels="localRoom.labels"
          @add-label="value => $emit('label-add', room, value)"
          @remove-label="value => $emit('label-remove', room, value)"
          @set-labels="parts => $emit('label-set', room, parts)"
      />

      <!-- Room Entities -->
      <Entities
          :editable="editable"
          :removingId="removingEntityId"
          :room="room"
          @player-click="onPlayerClick"
          @npc-click="onNpcClick"
          @remove-entity="(id, entity) => $emit('entity-removed', room, id, entity)"
      />

      <!-- Room Spawners -->
      <div class="info-section">
        <h4 class="gm-label">Spawners ({{ room.spawners?.length || 0 }})</h4>
        <div v-if="room.spawners && room.spawners.length > 0" class="spawners-list">
          <div v-for="spawner in room.spawners" :key="spawner.id" class="spawner-row">
            <div class="spawner-info">
              <div class="spawner-main">
                <span class="spawner-type">{{ getSpawnerTypeName(spawner) }}</span>
                <span class="gm-chip spawner-status" :class="{ 'is-active': spawner.is_active, 'gm-chip--muted': !spawner.is_active }">
                  {{ spawner.is_active ? 'Active' : 'Inactive' }}
                </span>
              </div>
              <div class="spawner-details">
                <span class="spawner-limit">Limit: {{ spawner.spawn_limit || 1 }}</span>
                <span class="spawner-cycles">Cycles: {{ spawner.respawn_cycles || 1 }}</span>
                <span class="spawner-spawned">Spawned: {{ spawner.spawned_entities?.length || 0 }}</span>
              </div>
            </div>
            <div class="spawner-actions">
              <button
                v-if="editable"
                class="row-action spawner-btn setup-btn"
                @click="openSpawnerSetup(spawner)"
                title="Configure this spawner">
                <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><circle cx="8" cy="8" r="2.2"/><path d="M8 1.8v1.6M8 12.6v1.6M14.2 8h-1.6M3.4 8H1.8M12.4 3.6l-1.1 1.1M4.7 11.3l-1.1 1.1M12.4 12.4l-1.1-1.1M4.7 4.7L3.6 3.6"/></svg>
              </button>
              <EditInDjangoAdmin
                v-if="editable"
                :app="spawner.object_type?.app_label || 'spawner'"
                :model="spawner.object_type?.model || 'npcspawner'"
                :id="spawner.id"
                class="spawner-admin-btn"
              />
              <button
                v-if="editable"
                class="row-action spawner-btn"
                :class="spawner.is_active ? 'deactivate-btn' : 'activate-btn'"
                @click="toggleSpawnerStatus(spawner)"
                :title="spawner.is_active ? 'Stop this spawner' : 'Start this spawner'">
                <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true">
                  <path v-if="spawner.is_active" d="M6.2 4v8M9.8 4v8"/>
                  <path v-else d="M5.5 3.8l7 4.2-7 4.2z"/>
                </svg>
              </button>
              <button
                v-if="editable"
                class="row-action row-action--danger spawner-btn delete-btn"
                @click="confirmDeleteSpawner(spawner)"
                title="Delete this spawner">
                <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 5h9M6.5 5V3.5h3V5M4.5 5l.6 8h5.8l.6-8"/></svg>
              </button>
            </div>
          </div>
        </div>
        <p v-else class="empty-note">No spawners here yet.</p>
        <div v-if="editable" class="spawner-create-section">
          <button class="gm-btn gm-btn--sm gm-btn--primary create-spawner-btn" @click="openCreateSpawner">
            <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M8 3.5v9M3.5 8h9"/></svg> Create spawner
          </button>
        </div>
      </div>

      <!-- Room Connections -->
      <Connections
          :room="room"
          :room-connections="roomConnections"
          :editor-state="editorState"
          :editable="editable"
          @open-connection-info="openConnectionInfo"
          @remove-connection="removeConnection"
      />

      <!-- Connection Information Modal -->
      <WorldEditorConnectionInfo
          v-if="selectedConnection"
          :connection="selectedConnection"
          :editable="editable"
          :editorState="editorState"
          @close="selectedConnection = null"
          @connection-updated="onConnectionUpdated"
          @connection-deleted="removeConnection"
          @view-room="viewRoom"
      />

      <!-- Room Actions -->
      <div v-if="editable" class="info-section">
        <h4 class="gm-label">Actions</h4>
        <div class="action-buttons">
          <button class="gm-btn gm-btn--sm action-btn" @click="duplicateRoom">
            <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><rect x="5.5" y="5.5" width="8" height="8" rx="1.5"/><path d="M10.5 5.5V4a1.5 1.5 0 00-1.5-1.5H4A1.5 1.5 0 002.5 4v5A1.5 1.5 0 004 10.5h1.5"/></svg> Duplicate room
          </button>
          <button class="gm-btn gm-btn--sm gm-btn--danger action-btn" @click="confirmDelete">
            <svg class="glyph" viewBox="0 0 16 16" aria-hidden="true"><path d="M3.5 5h9M6.5 5V3.5h3V5M4.5 5l.6 8h5.8l.6-8"/></svg> Delete room
          </button>
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm" class="modal-overlay" @click="cancelDelete">
      <div class="gm-panel gm-panel--raised modal-content" @click.stop>
        <h3 class="gm-section-title">Confirm Delete</h3>
        <p>Are you sure you want to delete this room? This action cannot be undone.</p>
        <div class="modal-actions">
          <button class="gm-btn gm-btn--sm" @click="cancelDelete">Cancel</button>
          <button class="gm-btn gm-btn--sm gm-btn--danger" @click="deleteRoom">Delete</button>
        </div>
      </div>
    </div>


    <!-- Delete Spawner Confirmation Modal -->
    <div v-if="showDeleteSpawnerConfirm" class="modal-overlay" @click="cancelDeleteSpawner">
      <div class="gm-panel gm-panel--raised modal-content" @click.stop>
        <h3 class="gm-section-title">Confirm Delete Spawner</h3>
        <p>Are you sure you want to delete this spawner? This action cannot be undone.</p>
        <div class="modal-actions">
          <button class="gm-btn gm-btn--sm" @click="cancelDeleteSpawner">Cancel</button>
          <button class="gm-btn gm-btn--sm gm-btn--danger" @click="deleteSpawner">Delete</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import WorldEditorConnectionInfo from '@/components/WorldEditor/WorldEditorConnectionInfo.vue';
import EditInDjangoAdmin from '@/components/EditInDjangoAdmin.vue';
import {gameMasterItemSpawnerService} from '@/services/GameMasterItemSpawnerService.js';
import {dragDropService} from '@/services/DragDropService.js';
import {characterTemplatesService} from '@/services/CharacterTemplatesService.ts';
import {GMWorldGenericSpawnersApi, GMWorldNPCSpawnersApi} from '@/api/backendService.js';

// Import the new components
import BasicInformation from '@/components/WorldEditor/WorldEditorRoomInfo/BasicInformation.vue';
import Labels from '@/components/WorldEditor/WorldEditorRoomInfo/Labels.vue';
import Entities from '@/components/WorldEditor/WorldEditorRoomInfo/Entities.vue';
import Connections from '@/components/WorldEditor/WorldEditorRoomInfo/Connections.vue';

export default {
  name: 'WorldEditorRoomInfo',
  components: {
    WorldEditorConnectionInfo,
    EditInDjangoAdmin,
    BasicInformation,
    Labels,
    Entities,
    Connections
  },
  props: {
    room: {
      type: Object,
      required: true
    },
    editable: {
      type: Boolean,
      default: false
    },
    labelCatalog: {
      type: Object,
      default: null
    },
    labelConflicts: {
      type: Array,
      default: () => []
    },
    labelBusy: {
      type: Boolean,
      default: false
    },
    removingEntityId: {
      type: String,
      default: null
    },
    editorState: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      localRoom: null,
      showDeleteConfirm: false,
      selectedConnection: null,
      isDraggingItem: false, // Flag to track if an item is being dragged
      // Spawner-related data
      showDeleteSpawnerConfirm: false,
      spawnerToDelete: null
    };
  },
  computed: {
    roomConnections() {
      if (!this.room || !this.localRoom) {
        return [];
      }

      // Use the connections array from the localRoom
      return this.localRoom.connections || [];
    }
  },
  watch: {
    room: {
      handler(newRoom) {
        this.localRoom = JSON.parse(JSON.stringify(newRoom));
      },
      immediate: true,
      deep: true
    }
  },

  created() {
    // No initialization needed here anymore
  },
  mounted() {
    // Register the drop zone with the drag drop service
    if (this.$refs.dropZone) {
      dragDropService.registerDropTarget(this.$refs.dropZone);
    }
  },
  beforeUnmount() {
    // Unregister the drop zone
    if (this.$refs.dropZone) {
      dragDropService.unregisterDropTarget(this.$refs.dropZone);
    }
  },
  methods: {
    // Methods for handling events from child components
    updateRoomType(newType) {
      this.localRoom.type = newType;
      this.onRoomUpdated();
    },

    closePanel() {
      this.$emit('close');
    },



    onPlayerClick(player) {
      // Emit an event to open the character card for this player
      if (player && player.id && player.object_type?.model === 'character') {
        this.$emit('character-selected', player.id);
      }
    },

    onNpcClick(npc) {
      // Emit an event to open the character card for this NPC
      if (npc && npc.id && npc.object_type?.model === 'character') {
        this.$emit('character-selected', npc.id);
      }
    },

    onRoomUpdated() {
      this.$emit('room-updated', this.room, {
        type: this.localRoom.type,
        labels: this.localRoom.labels.filter(label => label.trim() !== '')
      });
    },



    openConnectionInfo(connection) {
      if (!connection) {
        console.error('Cannot open connection info: connection is undefined');
        return;
      }
      this.selectedConnection = connection;
    },

    onConnectionUpdated(connectionId, updates) {
      if (!connectionId) {
        console.error('Cannot update connection: connectionId is undefined');
        return;
      }

      // Emit an event to update the connection in the parent component
      this.$emit('connection-updated', connectionId, updates);
    },

    removeConnection(connection) {
      if (!connection) {
        console.error('Cannot remove connection: connection is undefined');
        return;
      }

      // If connection is an object, extract the ID
      let connectionId;
      if (typeof connection === 'object') {
        if (!connection.id) {
          console.error('Cannot remove connection: connection.id is undefined');
          return;
        }
        connectionId = connection.id;
      } else {
        connectionId = connection;
      }

      this.$emit('connection-removed', connectionId);

      // If the selected connection is being removed, close the modal
      if (this.selectedConnection && this.selectedConnection.id === connectionId) {
        this.selectedConnection = null;
      }
    },

    viewRoom(roomId) {
      if (!roomId) {
        console.error('Cannot view room: roomId is undefined');
        return;
      }

      if (!this.editorState || !this.editorState.rooms) {
        console.error('Cannot view room: editorState or editorState.rooms is undefined');
        return;
      }

      // Find the room in the editor state
      const room = this.editorState.rooms.get(roomId);
      if (room) {
        // Emit an event to select the room in the parent component
        this.$emit('room-selected', room);
      } else {
        console.error(`Cannot view room: room with id ${roomId} not found`);
      }
    },

    duplicateRoom() {
      this.$emit('room-duplicated', this.room);
    },

    confirmDelete() {
      this.showDeleteConfirm = true;
    },

    cancelDelete() {
      this.showDeleteConfirm = false;
    },

    // Spawner Management Methods
    openCreateSpawner() {
      // Emit event to open spawner modal for creating new spawner
      this.$emit('open-spawner-modal', {
        positionId: this.room.id,
        npcSpawnerId: null
      });
    },

    openSpawnerSetup(spawner) {
      // Emit event to open spawner modal for editing existing spawner
      this.$emit('open-spawner-modal', {
        positionId: this.room.id,
        npcSpawnerId: spawner.id
      });
    },

    async toggleSpawnerStatus(spawner) {
      try {
        if (spawner.is_active) {
          // Deactivate spawner
          await GMWorldGenericSpawnersApi.gamemasterSpawnersAllDeactivateCreate(spawner.id);
          spawner.is_active = false;
        } else {
          // Activate spawner
          await GMWorldGenericSpawnersApi.gamemasterSpawnersAllActivateCreate(spawner.id);
          spawner.is_active = true;
        }

        // Update the local room data
        this.updateLocalRoomSpawner(spawner);

        console.log(`Spawner ${spawner.id} ${spawner.is_active ? 'activated' : 'deactivated'}`);
      } catch (error) {
        console.error('Failed to toggle spawner status:', error);
        // Revert the status change on error
        spawner.is_active = !spawner.is_active;
      }
    },

    confirmDeleteSpawner(spawner) {
      this.spawnerToDelete = spawner;
      this.showDeleteSpawnerConfirm = true;
    },

    cancelDeleteSpawner() {
      this.showDeleteSpawnerConfirm = false;
      this.spawnerToDelete = null;
    },

    async deleteSpawner() {
      if (!this.spawnerToDelete) return;

      try {
        await GMWorldNPCSpawnersApi.gamemasterSpawnersNpcDestroy(this.spawnerToDelete.id);

        // Remove spawner from local room data
        const spawnerIndex = this.localRoom.spawners.findIndex(s => s.id === this.spawnerToDelete.id);
        if (spawnerIndex !== -1) {
          this.localRoom.spawners.splice(spawnerIndex, 1);
        }

        console.log('Spawner deleted:', this.spawnerToDelete.id);
        this.cancelDeleteSpawner();

        // Refresh room data
        this.$emit('refresh-room');
      } catch (error) {
        console.error('Failed to delete spawner:', error);
      }
    },

    // Helper Methods
    getSpawnerTypeName(spawner) {
      if (spawner.object_type?.name) {
        return spawner.object_type.name;
      }
      if (spawner.object_type?.model) {
        return spawner.object_type.model.replace(/([A-Z])/g, ' $1').trim();
      }
      if (spawner.spawner_type) {
        return spawner.spawner_type.replace(/([A-Z])/g, ' $1').trim();
      }
      return 'Unknown Spawner';
    },

    updateLocalRoomSpawner(updatedSpawner) {
      const spawnerIndex = this.localRoom.spawners.findIndex(s => s.id === updatedSpawner.id);
      if (spawnerIndex !== -1) {
        // Update the spawner in the local room data
        this.localRoom.spawners[spawnerIndex] = { ...this.localRoom.spawners[spawnerIndex], ...updatedSpawner };
      }
    },

    deleteRoom() {
      this.showDeleteConfirm = false;
      this.$emit('room-deleted', this.room);
    },

    // Handle drag enter event
    handleDragEnter(event) {
      console.log('Drag enter on room info');
      const dropZone = event.currentTarget.closest('.room-drop-zone');
      if (dropZone) {
        dropZone.classList.add('drag-active');
      }
    },

    // Handle drag leave event
    handleDragLeave(event) {
      console.log('Drag leave on room info');
      const dropZone = event.currentTarget.closest('.room-drop-zone');
      if (dropZone) {
        // Only remove if we're actually leaving the drop zone area
        // Check if the related hasTarget is outside the drop zone
        if (!dropZone.contains(event.relatedTarget)) {
          dropZone.classList.remove('drag-active');
        }
      }
    },

    // Handle entity (item or NPC) drop event
    async handleEntityDrop(event) {
      console.log('Drop event triggered on room info');
      const dropZone = event.currentTarget.closest('.room-drop-zone');

      try {
        // Prevent default to stop browser from opening the dragged item
        event.preventDefault();

        // Remove active state immediately
        if (dropZone) {
          dropZone.classList.remove('drag-active');
        }

        // Get the dropped data
        console.log('Event dataTransfer types:', event.dataTransfer.types);

        // Check if this is an NPC template
        const isNpcTemplate = event.dataTransfer.types.includes('application/npc-template');

        // Try to get the data from different MIME types
        let itemData;
        if (isNpcTemplate) {
          itemData = event.dataTransfer.getData('application/npc-template');
        } else {
          itemData = event.dataTransfer.getData('application/json');
          if (!itemData) {
            itemData = event.dataTransfer.getData('text/plain');
          }
        }

        console.log('Got data from drop event:', itemData);

        if (!itemData) {
          console.warn('No data received from drop event');
          return;
        }

        // Try to parse the data
        const droppedEntity = JSON.parse(itemData);
        console.log('Parsed data:', droppedEntity);

        if (!droppedEntity || !droppedEntity.id) {
          console.warn('Invalid data received:', itemData);
          return;
        }

        console.log('Entity dropped on room:', droppedEntity, this.room);

        // Show success feedback
        if (dropZone) {
          dropZone.classList.add('drop-success');

          // Remove the success feedback after a short delay
          setTimeout(() => {
            dropZone.classList.remove('drop-success');
          }, 1000);
        }

        // Handle based on entity type
        if (this.room && this.room.id) {
          let result;

          if (isNpcTemplate) {
            // Create position object for the NPC
            const position = {
              id: this.room.id,
              grid_x: this.room.position.grid_x,
              grid_y: this.room.position.grid_y,
              grid_z: this.room.position.grid_z
            };

            // Use CharacterTemplatesService to create an NPC from the template
            result = await characterTemplatesService.createNpcFromTemplate(droppedEntity.id, position);
            console.log('NPC created successfully:', result);

            // Update the room's NPC count without triggering a full map reload
            if (this.room.npcs && result) {
              // Add the new NPC to the room's npcs array
              this.room.npcs.push({
                id: result.id,
                name: droppedEntity.name,
                behavior: droppedEntity.behavior,
                object_type: {
                  app_label: 'world',
                  model: 'character'
                }
              });

              // Emit an event to notify parent components that the room has been updated
              this.$emit('room-npcs-updated', this.room);
            }
          } else {
            // Use the GameMasterItemSpawnerService to spawn the item at the room's position
            result = await gameMasterItemSpawnerService.spawnItemToPosition(droppedEntity.id, this.room.id);

            if (result) {
              console.log('Item spawned successfully:', result);

              // Update the room's object count without triggering a full map reload
              if (this.room.objects) {
                // Add the new object to the room's objects array
                this.room.objects.push({
                  id: result.id || result.world_item?.id,
                  name: droppedEntity.name,
                  type: droppedEntity.type,
                  object_type: {
                    app_label: 'world',
                    model: 'gameobject'
                  }
                });

                // Emit an event to notify parent components that the room has been updated
                this.$emit('room-objects-updated', this.room);
              }
            }
          }
        }

        // Notify the drag drop service that we've ended dragging
        dragDropService.endDrag();
      } catch (error) {
        console.error('Error handling dropped item:', error);

        // Remove any active states on error
        if (dropZone) {
          dropZone.classList.remove('drag-active', 'drop-success');
        }
      }
    },

    // Optional: Method to handle global drag state changes
    // Call this when drag starts globally to show light highlight
    onGlobalDragStart() {
      const dropZone = this.$refs.dropZone;
      if (dropZone) {
        dropZone.classList.add('drag-over-highlight');
      }
    },

    // Optional: Method to handle global drag end
    // Call this when drag ends globally to remove highlights
    onGlobalDragEnd() {
      const dropZone = this.$refs.dropZone;
      if (dropZone) {
        dropZone.classList.remove('drag-over-highlight', 'drag-active', 'drop-success');
      }
    },

  }
};
</script>

<style scoped>
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.world-editor-room-info {
  background: var(--gm-surface-1);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
}

/* Full Container Drop Zone */
.room-drop-zone {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
  border-radius: 4px;
  pointer-events: none;
}

/* Drop Zone Overlay - Hidden by default */
.drop-zone-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--gm-accent-soft);
  border: 2px solid var(--gm-border);
  border-radius: var(--gm-radius);
  display: none; /* Hidden by default */
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

/* Show overlay when dragging over */
.room-drop-zone.drag-active .drop-zone-overlay {
  display: flex; /* Show when active */
}

/* Optional: Show overlay when any dragging is happening (lighter version) */
.room-drop-zone.drag-over-highlight .drop-zone-overlay {
  display: flex;
  background-color: var(--gm-accent-softer);
  border: 1px solid var(--gm-border-dim);
  pointer-events: all;
}

/* Drop Zone Message */
.drop-zone-message {
  background-color: rgba(0, 0, 0, 0.8);
  border-radius: var(--gm-radius);
  padding: var(--gm-gap) var(--gm-gap-lg);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--gm-gap-sm);
  transform: scale(0.96);
  transition: transform var(--gm-duration-ambient) var(--gm-ease);
}

.room-drop-zone.drag-active .drop-zone-message {
  transform: scale(1);
}

.drop-zone-icon {
  width: 26px;
  height: 26px;
  fill: none;
  stroke: var(--gm-accent);
  stroke-width: 1.3;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.drop-zone-text {
  color: var(--gm-text);
  font-family: var(--gm-font-display);
  font-weight: 600;
  font-size: var(--gm-text-control);
  letter-spacing: var(--gm-track-label);
  text-transform: uppercase;
}

/* Visual feedback for successful drop */
.room-drop-zone.drop-success .drop-zone-overlay {
  display: flex;
  background-color: rgba(143, 182, 60, 0.2);
  border: 2px solid var(--gm-success);
}

.room-drop-zone.drop-success .drop-zone-message {
  background-color: var(--gm-success);
}

.room-drop-zone.drop-success .drop-zone-text {
  color: var(--gm-text-on-accent);
}

.room-drop-zone.drop-success .drop-zone-icon {
  color: var(--gm-text-on-accent);
}

.room-info-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: var(--gm-gap);
  background: var(--gm-surface-2);
  border-bottom: 1px solid var(--gm-border-dim);
}

.room-info-content {
  flex: 1;
  padding: var(--gm-gap-sm);
  overflow-y: auto;
  overscroll-behavior: contain;
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-sm);
}

/* Info Sections */
.info-section {
  background: var(--gm-surface-2);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius);
  padding: var(--gm-gap);
}

.info-section h4 {
  margin: 0 0 var(--gm-gap-sm);
}

/* Spawners. These classes existed in the template with nothing defining them,
   so the list rendered as bare stacked divs. */
.spawners-list {
  display: flex;
  flex-direction: column;
  gap: var(--gm-gap-xs);
}

.spawner-row {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  padding: var(--gm-gap-sm);
  background: var(--gm-surface-sunken);
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  transition: border-color var(--gm-duration) var(--gm-ease);
}

.spawner-row:hover {
  border-color: var(--gm-border);
}

.spawner-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.spawner-main {
  display: flex;
  align-items: center;
  gap: var(--gm-gap-sm);
  min-width: 0;
}

.spawner-type {
  flex: 1;
  min-width: 0;
  font-size: var(--gm-text-sm);
  color: var(--gm-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.spawner-status {
  flex: none;
}

.spawner-details {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gm-gap-sm);
  font-size: var(--gm-text-micro);
  color: var(--gm-text-dim);
  font-variant-numeric: tabular-nums;
}

.spawner-actions {
  flex: none;
  display: flex;
  align-items: center;
  gap: 2px;
}

/* Row-level destructive actions stay quiet until reached for — a list of red
   boxes competes with the values it is meant to act on. */
.row-action {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  width: 22px;
  height: 22px;
  padding: 0;
  background: transparent;
  border: 1px solid var(--gm-border-dim);
  border-radius: var(--gm-radius-sm);
  color: var(--gm-text-dim);
  cursor: pointer;
  transition: color var(--gm-duration) var(--gm-ease),
              border-color var(--gm-duration) var(--gm-ease),
              background var(--gm-duration) var(--gm-ease);
}

.row-action:hover {
  color: var(--gm-accent);
  border-color: var(--gm-border);
  background: var(--gm-accent-softer);
}

.row-action:focus-visible {
  outline: none;
  border-color: var(--gm-border-hi);
}

.row-action--danger:hover {
  color: var(--gm-danger);
  border-color: var(--gm-danger);
  background: rgba(201, 69, 63, 0.12);
}

.empty-note {
  margin: 0;
  color: var(--gm-text-dim);
  font-size: var(--gm-text-xs);
}

.spawner-create-section {
  margin-top: var(--gm-gap-sm);
}

/* Action Buttons */
.action-buttons {
  display: flex;
  gap: var(--gm-gap-sm);
}

.action-btn {
  flex: 1;
}

/* Modal */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-content {
  padding: var(--gm-gap-lg);
  max-width: 400px;
  width: 90%;
}

.modal-content p {
  margin: 0 0 var(--gm-gap-lg) 0;
  color: var(--gm-text-muted);
  line-height: 1.4;
}

.modal-actions {
  display: flex;
  gap: var(--gm-gap-sm);
  justify-content: flex-end;
}

/* Scrollbar Styling */
.room-info-content::-webkit-scrollbar {
  width: 6px;
}

.room-info-content::-webkit-scrollbar-track {
  background: var(--gm-surface-1);
}

.room-info-content::-webkit-scrollbar-thumb {
  background: var(--gm-border);
  border-radius: var(--gm-radius-pill);
}

/* Responsive Design */
@media (max-width: 768px) {
  .action-buttons {
    flex-direction: column;
  }
}

.glyph {
  width: 12px;
  height: 12px;
  flex: none;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.close-btn,
.spawner-btn,
.create-spawner-btn,
.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
}
</style>
